// Moved from `src/hooks/query/useCombinedQueriesTeamGeneralInfo.ts` (now a
// bare re-export) with OT3 slice 2 (ARCFE-955). Data projection over three
// package queries. The kinde_user_id read is a data PROJECTION over the user
// response (OT3's own note: data, not SDK); no auth-provider import exists
// here.
//
// The team-details error toast used to thread through here as an
// `onTeamDetailsFetchError` option so a composer-triggered fetch kept the app
// reaction (ARCFE-921). It is bound at `configureWorkspaceData` now
// now, so this projection carries no options at all and every observer
// of the inner query gets the same reaction.
import { RoleCategory, UserRole } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';
import { useQueryJoinedTeams } from './useQueryJoinedTeams';
import { useQueryTeamDetails } from './useQueryTeamDetails';
import { useQueryUser } from './useQueryUser';

export const useCombinedQueriesTeamGeneralInfo = () => {
  const {
    data: joinedTeamsData,
    isFetched: isJoinedTeamsFetched,
    isFetching: isJoinedTeamsFetching,
    isPending: isJoinedTeamsPending,
  } = useQueryJoinedTeams();
  const {
    data: teamDetails,
    isLoading: isLoadingTeamDetails,
    refetch: refetchTeamDetails,
  } = useQueryTeamDetails(joinedTeamsData?.joined_teams?.at(0)?.team_id);
  const { data: kindeUserId } = useQueryUser((data) => data?.kinde_user_id);

  const teamOrgId = useMemo(() => teamDetails && teamDetails?.team?.id, [teamDetails]);

  const currentMember = useMemo(
    () =>
      teamDetails?.team?.team_members?.find(
        (member) => member.kinde_user_id === kindeUserId
      ),
    [teamDetails, kindeUserId]
  );
  const isTeamAdmin = useMemo(
    () =>
      currentMember?.user_role_list?.some((role) => role.category === RoleCategory.Admin),
    [currentMember]
  );

  const isSupportRole = useMemo(
    () =>
      currentMember?.user_role_list?.some(
        (role) => role.category === RoleCategory.Support
      ),
    [currentMember]
  );

  const teamAdminIDList = useMemo(
    () =>
      teamDetails?.team?.team_members
        ?.filter((member) => member.user_role === UserRole.ADMIN)
        .map((member) => member.kinde_user_id),
    [teamDetails]
  );

  const isTeamOwner = useMemo(
    () => teamDetails?.team?.owner_kinde_id === kindeUserId,
    [teamDetails, kindeUserId]
  );

  const hasTeam = useMemo(
    // `!isEmpty(arr)` verbatim for an array-typed field, without pulling
    // lodash-es (ESM-only) into a package consumed by other packages' suites.
    () => (joinedTeamsData?.joined_teams?.length ?? 0) > 0,
    [joinedTeamsData]
  );

  const isTeamMember = useMemo(
    () => currentMember?.user_role === UserRole.MEMBER,
    [currentMember]
  );

  const userRoleInTeam = useMemo(() => currentMember?.user_role, [currentMember]);

  const userRoleList = useMemo(() => currentMember?.user_role_list, [currentMember]);

  const userRoleIds = useMemo(() => userRoleList?.map((role) => role.id), [userRoleList]);

  const isNonOwnerTeamMember = useMemo(
    () => hasTeam && !isTeamOwner,
    [hasTeam, isTeamOwner]
  );

  const isNonAdminTeamMember = useMemo(
    () => hasTeam && !isTeamAdmin,
    [hasTeam, isTeamAdmin]
  );

  // Consider teams fetched when all relevant queries are complete
  const isTeamsFetched = isJoinedTeamsFetched;

  // All team-derived role values (isSupportRole, isTeamAssistant, userRoleIds, etc.)
  // flow through currentMember, which requires both teamDetails AND kindeUserId.
  // This flag is true when all three underlying queries have settled, closing the
  // race where isLoadingTeamDetails is false but kindeUserId hasn't arrived yet.
  const isTeamDataReady =
    isJoinedTeamsFetched && !isLoadingTeamDetails && kindeUserId != null;

  const isOnlySupportRole = useMemo(
    () => userRoleList?.every((role) => role.category === RoleCategory.Support) ?? false,
    [userRoleList]
  );

  const isTeamAssistant = useMemo(
    () =>
      Boolean(currentMember?.user_role_list?.find((role) => role.name === 'Assistant')),
    [currentMember]
  );

  const isTeamStripeManaged = useMemo(
    () => teamDetails?.team?.is_stripe_managed ?? false,
    [teamDetails]
  );

  return {
    isLoadingTeamDetails,
    refetchTeamDetails,
    isJoinedTeamsFetched,
    isJoinedTeamsFetching,
    isJoinedTeamsPending,
    isTeamDataReady,
    joinedTeamsData,
    teamDetails,
    hasTeam,
    teamOrgId,
    isTeamAdmin,
    isSupportRole,
    teamAdminIDList,
    isTeamOwner,
    isTeamMember,
    userRoleInTeam,
    userRoleList,
    userRoleIds,
    isNonOwnerTeamMember,
    isNonAdminTeamMember,
    isTeamsFetched,
    isOnlySupportRole,
    isTeamAssistant,
    isTeamStripeManaged,
  };
};
